import { createTheme } from "@mui/material";
import type {} from "@mui/x-data-grid/themeAugmentation";

const FONT =
  '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif';

export const theme = createTheme({
  status: {
    danger: "#DC2626",
    success: "#16A34A",
  },
  palette: {
    primary: {
      main: "#303398",
      light: "#EEF0FB",
      dark: "#1F2266",
      contrastText: "#fff",
    },
    secondary: {
      main: "#F6821F",
      light: "#F4F6FA",
      dark: "#C2610A",
    },
    success: { main: "#16A34A" },
    warning: { main: "#D97706" },
    error: { main: "#DC2626" },
    background: { default: "#F5F7FB", paper: "#FFFFFF" },
    text: { primary: "#0F172A", secondary: "#64748B" },
    divider: "#E5E9F0",
    greyColors: {
      main: "#CBD5E1",
      lightGrey: "#E2E8F0",
    },
  },
  shape: { borderRadius: 10 },
  mixins: { toolbar: { minHeight: 72 } },
  typography: {
    fontFamily: FONT,
    h4: { fontWeight: 700, letterSpacing: "-0.02em" },
    h5: { fontWeight: 700, letterSpacing: "-0.015em" },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: "#F5F7FB", WebkitFontSmoothing: "antialiased" },
        "*": { scrollbarWidth: "thin", scrollbarColor: "#CBD5E1 transparent" },
        "*::-webkit-scrollbar": { width: 8, height: 8 },
        "*::-webkit-scrollbar-track": { background: "transparent" },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "#CBD5E1",
          borderRadius: 8,
          border: "2px solid transparent",
          backgroundClip: "content-box",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#94A3B8",
          backgroundClip: "content-box",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, padding: "8px 18px", transition: "all .15s ease" },
        sizeSmall: { padding: "4px 12px" },
        sizeLarge: { padding: "11px 24px", fontSize: 15 },
        contained: {
          "&:hover": { boxShadow: "0 6px 16px rgba(48,51,152,0.25)" },
        },
        containedSecondary: {
          color: "#fff",
          "&:hover": { boxShadow: "0 6px 16px rgba(246,130,31,0.30)" },
        },
        outlined: {
          borderColor: "#D5DBE5",
          color: "#1E293B",
          "&:hover": { borderColor: "#303398", backgroundColor: "#EEF0FB" },
        },
        text: { "&:hover": { backgroundColor: "#EEF0FB" } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none" },
        outlined: { borderColor: "#E5E9F0" },
        rounded: { borderRadius: 14 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #E5E9F0",
          boxShadow: "0 1px 3px rgba(16,24,40,0.05), 0 1px 2px rgba(16,24,40,0.03)",
          borderRadius: 14,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "inherit" },
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(8px)",
          color: "#0F172A",
          borderBottom: "1px solid #E5E9F0",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRight: "1px solid #E5E9F0", backgroundColor: "#FFFFFF" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D5DBE5" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94A3B8" },
          "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(48,51,152,0.10)" },
        },
      },
    },
    MuiTextField: { defaultProps: { variant: "outlined" } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
    MuiStepIcon: {
      styleOverrides: {
        root: { "&.Mui-completed": { color: "#16A34A" }, "&.Mui-active": { color: "#303398" } },
      },
    },
    MuiLinearProgress: { styleOverrides: { root: { borderRadius: 8, height: 6 } } },
    MuiAlert: { styleOverrides: { root: { borderRadius: 12, alignItems: "center" } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 18, boxShadow: "0 24px 60px rgba(15,23,42,0.22)" } } },
    MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700 } } },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: "1px solid #E5E9F0",
          boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: "#0F172A", fontSize: 12, borderRadius: 6 },
        arrow: { color: "#0F172A" },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: 3 } } },
    MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 46 } } },
    MuiTableRow: { styleOverrides: { root: { "&:last-child td": { borderBottom: 0 } } } },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 600, color: "#64748B", backgroundColor: "#F8FAFC", fontSize: 13 },
        root: { borderColor: "#EDF0F5" },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: { fontFamily: FONT, border: "none" },
      },
    },
  },
});
